import React, { useCallback, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import useTranslation from '~/app/hooks/useTranslation';
import Icon from '~/app/common/Icon';
import { DateRefObject } from '~/app/modules/form/Date';
import { WorkOrderDetail, WorkOrderMechanic } from '~/workorder/types';
import {
    formatWorkOrderDetailDate,
    canEditWorkOrderMechanics,
    canEditWorkOrderPlannedDates,
    scheduledDateApiToPicker,
} from './helper';
import PlannedDateRange from './PlannedDateRange';
import classes from './style.module.scss';import { route } from '~/app/helpers/route';
import LinkWrapper from '~/app/common/LinkWrapper';
import { MACHINES_DETAILS_ROUTES } from '~/machines/Details/Router';
import SearchInput from '~/app/modules/form/Search';
import { API } from '~/app/routes';
import { AllUsers } from '~/types';
import { useStoreActions } from '~/store';
import { notify } from '~/app/helpers/notify';
import { useWorkOrderScheduledDateAutoSave } from '~/workorder/Detail/useWorkOrderScheduledDateAutoSave';

export type MainContentComponent = React.FC<{ data: WorkOrderDetail }>

const MainContent: MainContentComponent = ({ data }) => {
    const { t } = useTranslation('workorder');
    const updateWorkOrderMechanic = useStoreActions(actions => actions.workOrder.updateWorkOrderMechanic);

    const searchAnchorRef = useRef<HTMLDivElement>(null);
    const plannedStartDateRef = useRef<DateRefObject>(null);
    const plannedEndDateRef = useRef<DateRefObject>(null);
    const [pendingMechanicKey, setPendingMechanicKey] = useState<string | null>(null);
    const [mechanicSearchKey, setMechanicSearchKey] = useState(0);
    const [plannedDateSaving, setPlannedDateSaving] = useState(false);
    const statusClass = `status-${data.status}`;

    const workStarted = useMemo(
        () => formatWorkOrderDetailDate(data.actualStartDate),
        [data.actualStartDate],
    );

    const workFinished = useMemo(
        () => formatWorkOrderDetailDate(data.actualEndDate),
        [data.actualEndDate],
    );

    const responsibleName = data.personnel?.name?.trim();

    const canEditMechanics = useMemo(
        () => canEditWorkOrderMechanics(data.status),
        [data.status],
    );

    const canEditPlannedDates = useMemo(
        () => canEditWorkOrderPlannedDates(data.status),
        [data.status],
    );

    const plannedStartPickerDefault = useMemo(
        () => scheduledDateApiToPicker(data.scheduledStartDate),
        [data.scheduledStartDate],
    );

    const plannedEndPickerDefault = useMemo(
        () => scheduledDateApiToPicker(data.scheduledEndDate),
        [data.scheduledEndDate],
    );

    const {
        pickerValue: plannedStartPickerValue,
        handleChange: handlePlannedStartChange,
        handleBlur: handlePlannedStartBlur,
    } = useWorkOrderScheduledDateAutoSave({
        workOrderId: data.id,
        fieldKey: 'scheduledStartDate',
        savedValue: data.scheduledStartDate,
        peerSavedValue: data.scheduledEndDate,
        saving: plannedDateSaving,
        setSaving: setPlannedDateSaving,
        dateRef: plannedStartDateRef,
    });

    const {
        handleChange: handlePlannedEndChange,
        handleBlur: handlePlannedEndBlur,
    } = useWorkOrderScheduledDateAutoSave({
        workOrderId: data.id,
        fieldKey: 'scheduledEndDate',
        savedValue: data.scheduledEndDate,
        peerSavedValue: data.scheduledStartDate,
        saving: plannedDateSaving,
        setSaving: setPlannedDateSaving,
        dateRef: plannedEndDateRef,
    });

    const minPlannedEndDate = useMemo(() => {
        const start = plannedStartPickerValue || plannedStartPickerDefault;
        return start ? String(start) : undefined;
    }, [plannedStartPickerDefault, plannedStartPickerValue]);

    const focusMechanicSearch = useCallback(() => {
        const input = searchAnchorRef.current?.querySelector('input');
        input?.focus();
    }, []);

    const handleMechanicAdd = useCallback((user: AllUsers) => {
        const mechanicId = Number(user.id);
        const alreadyAdded = data.mechanics?.some(m => m.mechanicId === mechanicId);
        if (alreadyAdded) {
            notify(t('detail.error.mechanicAlreadyAdded'), 'error');
            return;
        }

        const pendingKey = `add-${mechanicId}`;
        setPendingMechanicKey(pendingKey);

        const payload = { id: 0, workOrderId: data.id, mechanicId, operation: 'add' as 'add' | 'remove' };
        updateWorkOrderMechanic(payload)
            .catch(() => notify(t('detail.error.mechanicUpdateFailed'), 'error'))
            .finally(() => {
                setPendingMechanicKey(null);
                setMechanicSearchKey(key => key + 1);
            });

    }, [data.id, data.mechanics, t, updateWorkOrderMechanic]);

    const handleMechanicRemove = useCallback((event: React.MouseEvent<HTMLButtonElement>, mechanic: WorkOrderMechanic) => {
        event.stopPropagation();
        const pendingKey = `remove-${mechanic.id}`;
        if (pendingMechanicKey) {
            return;
        }
        setPendingMechanicKey(pendingKey);

        const payload = { id: mechanic.id, workOrderId: data.id, mechanicId: mechanic.mechanicId, operation: 'remove' as 'add' | 'remove' };
        updateWorkOrderMechanic(payload)
            .catch(() => notify(t('detail.error.mechanicUpdateFailed'), 'error'))
            .finally(() => setPendingMechanicKey(null));
    }, [data.id, t, pendingMechanicKey, updateWorkOrderMechanic]);

    const isMechanicPending = useCallback((mechanic: WorkOrderMechanic) => (
        pendingMechanicKey === `remove-${mechanic.id}`
    ), [pendingMechanicKey]);

    const isAddPending = pendingMechanicKey?.startsWith('add-') ?? false;

    const plannedDatesDisabled = plannedDateSaving;

    return (
        <div className={classes['card']}>
            <div className={classes['header']}>
                <div className={classes['header-main']}>
                    <div className={classes['title-row']}>
                        <h1 className={classes['title']}>{data.excavator?.name}</h1>
                        <LinkWrapper to={route(MACHINES_DETAILS_ROUTES.details, { projectId: data.projectId ?? 0, id: data.excavator.id })} className={classes['launch-link']}>
                            <Icon name="launch" size={18} className={classes['launch-icon']} />
                        </LinkWrapper>
                    </div>
                    {data.excavator?.intern && (
                        <p className={classes['intern']}>{data.excavator.intern}</p>
                    )}
                </div>
                <span className={classNames(classes['status'], classes[statusClass])}>
                    {t(`statuses.${data.status}`)}
                </span>
            </div>

            <div className={classes['details']}>
                {responsibleName && (
                    <div className={classes['row']}>
                        <span className={classes['label']}>{t('detail.responsible')}</span>
                        <span className={classes['value']}>{responsibleName}</span>
                    </div>
                )}
                <div className={classes['row']}>
                    <span className={classes['label']}>{t('detail.employee')}</span>
                    <div className={classes['employee-value']}>
                        {data.mechanics?.map(mechanic => (
                            <span key={mechanic.id} className={classNames(classes['chip'], !canEditMechanics && classes['padding-right'])}>
                                <span className={classes['chip-label']}>{mechanic.name}</span>
                                {canEditMechanics && (
                                    <button
                                        type="button"
                                        className={classes['chip-remove']}
                                        onClick={e => handleMechanicRemove(e, mechanic)}
                                        disabled={isMechanicPending(mechanic) || !!pendingMechanicKey}
                                        aria-label={t('detail.removeEmployee')}
                                    >
                                        <Icon name="close" size={14} />
                                    </button>
                                )}
                            </span>
                        ))}
                        {canEditMechanics && (
                            <>
                                <button
                                    type="button"
                                    className={classes['add-chip']}
                                    onClick={focusMechanicSearch}
                                    disabled={isAddPending || !!pendingMechanicKey}
                                    aria-label={t('detail.searchEmployee')}
                                >
                                    <Icon name="add" size={16} />
                                </button>
                                <div ref={searchAnchorRef} className={classes['mechanic-search-anchor']}>
                                    <SearchInput<AllUsers>
                                        key={mechanicSearchKey}
                                        placeholder={t('detail.searchEmployee')}
                                        className={classes['mechanic-search-field']}
                                        searchUrl={API.allUsers}
                                        searchKey="name"
                                        renderItem={item => item.name}
                                        onSelect={handleMechanicAdd as any}
                                        iconPosition="left"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <div className={classes['row']}>
                    <span className={classes['label']}>{t('detail.planned')}</span>
                    <PlannedDateRange
                        canEdit={canEditPlannedDates}
                        scheduledStartDate={data.scheduledStartDate}
                        scheduledEndDate={data.scheduledEndDate}
                        plannedStartPickerDefault={plannedStartPickerDefault}
                        plannedEndPickerDefault={plannedEndPickerDefault}
                        minPlannedEndDate={minPlannedEndDate}
                        disabled={plannedDatesDisabled}
                        startDateRef={plannedStartDateRef}
                        endDateRef={plannedEndDateRef}
                        onStartChange={handlePlannedStartChange}
                        onStartBlur={handlePlannedStartBlur}
                        onEndChange={handlePlannedEndChange}
                        onEndBlur={handlePlannedEndBlur}
                    />
                </div>
                <div className={classes['row']}>
                    <span className={classes['label']}>{t('detail.workStarted')}</span>
                    <span className={classes['value']}>{workStarted ?? '-'}</span>
                </div>
                <div className={classes['row']}>
                    <span className={classes['label']}>{t('detail.workFinished')}</span>
                    <span className={classes['value']}>{workFinished ?? '-'}</span>
                </div>
            </div>

            <div className={classes['footer']}>
                {data.workOrderNo}
            </div>
        </div>
    );
};

export default MainContent;